import { type ReactNode, useCallback, useMemo, useState } from 'react';

import Loading from '@/components/molecules/Loading';
import { FullscreenLoadingContext } from './FullscreenLoadingContext';

/**
 * Renders a single, shared fullscreen <Loading /> instance for the whole app.
 *
 * Consumers signal loading via `acquire()` (see `useFullscreenLoading` /
 * `FullscreenLoadingBoundary`). The overlay stays mounted as long as at least
 * one consumer holds it, so when one part of the tree finishes loading and
 * another starts in the same commit, the count never reaches zero and the
 * spinner's CSS animation keeps running continuously instead of restarting.
 */
export function FullscreenLoadingProvider({
  children,
}: {
  children: ReactNode;
}) {
  const [count, setCount] = useState(0);

  const acquire = useCallback(() => {
    setCount((c) => c + 1);
    let released = false;
    return () => {
      if (released) return;
      released = true;
      setCount((c) => Math.max(0, c - 1));
    };
  }, []);

  const value = useMemo(() => ({ acquire }), [acquire]);

  return (
    <FullscreenLoadingContext value={value}>
      {children}
      {count > 0 && <Loading fullscreen />}
    </FullscreenLoadingContext>
  );
}
